package com.rosario.juridico.marketplace.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.domain.PerfilAbogadoDataModel
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.marketplace.viewmodel.UiState
import com.rosario.juridico.marketplace.viewmodel.DirectorioViewModel

private val VerdeVerificado = Color(0xFF1D9E75)
private val AzulPerfil = Color(0xFF1A7FD4)

private val ListaEspecialidadesPredefinidas = listOf(
    "Todas",
    "Laboral",
    "Civil y Comercial",
    "Familia",
    "Penal",
    "Contencioso Administrativo",
    "Ejecución Fiscal",
    "Concursos y Quiebras",
    "Sucesiones",
    "Daños y Perjuicios"
)

private val ListaLocalidadesPredefinidas = listOf(
    "Todas",
    "Rosario",
    "Santa Fe",
    "Venado Tuerto",
    "Rafaela",
    "Reconquista",
    "San Lorenzo",
    "Villa Constitución"
)

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun DirectorioAvvoScreen(
    navController: NavController,
    viewModel: DirectorioViewModel
) {
    val directorioState by viewModel.directorioAbogadosState.collectAsState()
    val filtroEspecialidad by viewModel.filtroEspecialidadDirectorio.collectAsState()
    val filtroLocalidad by viewModel.filtroLocalidadDirectorio.collectAsState()
    val queryBusqueda by viewModel.queryBusquedaDirectorio.collectAsState()

    var perfilSeleccionadoDetalle by remember { mutableStateOf<PerfilAbogadoDataModel?>(null) }

    LaunchedEffect(Unit) {
        viewModel.loadDirectorioAbogados()
    }

    Column(modifier = Modifier.fillMaxSize()) {
        MktHeader(
            titulo = "Directorio de Abogados Verificados",
            subtitulo = "Busca profesionales matriculados por especialidad y localidad",
            onBack = { navController.popBackStack() }
        )

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            // 🔍 Buscador de Texto Libre
            OutlinedTextField(
                value = queryBusqueda,
                onValueChange = { viewModel.setQueryBusquedaDirectorio(it) },
                placeholder = { Text("Buscar por nombre, especialidad...") },
                leadingIcon = { Icon(Icons.Default.Search, contentDescription = null, tint = VerdeVerificado) },
                trailingIcon = {
                    if (queryBusqueda.isNotBlank()) {
                        IconButton(onClick = { viewModel.setQueryBusquedaDirectorio("") }) {
                            Icon(Icons.Default.Close, contentDescription = "Limpiar")
                        }
                    }
                },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(14.dp),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = VerdeVerificado,
                    unfocusedBorderColor = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                )
            )

            // 🎯 Filtro Horizontal: Especialidades
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = "Especialidad:",
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    contentPadding = PaddingValues(horizontal = 2.dp)
                ) {
                    items(ListaEspecialidadesPredefinidas) { esp ->
                        val isSelected = filtroEspecialidad == esp
                        FilterChip(
                            selected = isSelected,
                            onClick = { viewModel.setFiltroEspecialidadDirectorio(esp) },
                            label = { Text(esp, fontSize = 12.sp) },
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = VerdeVerificado,
                                selectedLabelColor = Color.White,
                                containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.6f)
                            ),
                            shape = RoundedCornerShape(20.dp)
                        )
                    }
                }
            }

            // 📍 Filtro Horizontal: Localidades / Jurisdicciones
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = "Localidad / Zona:",
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    contentPadding = PaddingValues(horizontal = 2.dp)
                ) {
                    items(ListaLocalidadesPredefinidas) { loc ->
                        val isSelected = filtroLocalidad == loc
                        FilterChip(
                            selected = isSelected,
                            onClick = { viewModel.setFiltroLocalidadDirectorio(loc) },
                            label = { Text(loc, fontSize = 12.sp) },
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = AzulPerfil,
                                selectedLabelColor = Color.White,
                                containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.6f)
                            ),
                            shape = RoundedCornerShape(20.dp)
                        )
                    }
                }
            }
        }

        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))

        // 📋 Lista del Directorio
        when (val state = directorioState) {
            is UiState.Loading -> {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = VerdeVerificado)
                }
            }
            is UiState.Error -> {
                Box(modifier = Modifier.fillMaxSize().padding(24.dp), contentAlignment = Alignment.Center) {
                    Text(
                        text = "Error al cargar el directorio: ${state.message}",
                        color = MaterialTheme.colorScheme.error,
                        textAlign = TextAlign.Center
                    )
                }
            }
            is UiState.Success -> {
                val abogados = state.data
                if (abogados.isEmpty()) {
                    Box(modifier = Modifier.fillMaxSize().padding(24.dp), contentAlignment = Alignment.Center) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Icon(
                                imageVector = Icons.Default.SearchOff,
                                contentDescription = null,
                                modifier = Modifier.size(54.dp),
                                tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f)
                            )
                            Text(
                                text = "No se encontraron abogados verificados",
                                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Text(
                                text = "Intenta ajustar o limpiar los filtros de búsqueda.",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                            )
                            Spacer(Modifier.height(8.dp))
                            OutlinedButton(
                                onClick = {
                                    viewModel.setFiltroEspecialidadDirectorio("Todas")
                                    viewModel.setFiltroLocalidadDirectorio("Todas")
                                    viewModel.setQueryBusquedaDirectorio("")
                                },
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                Text("Restablecer Filtros")
                            }
                        }
                    }
                } else {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 12.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        items(abogados) { abogado ->
                            AbogadoDirectorioCard(
                                abogado = abogado,
                                onVerDetalle = { perfilSeleccionadoDetalle = abogado }
                            )
                        }
                    }
                }
            }
            else -> {}
        }
    }

    // 📄 Diálogo con el Perfil Completo y Ficha Pública del Profesional
    if (perfilSeleccionadoDetalle != null) {
        FichaPerfilAbogadoDialog(
            abogado = perfilSeleccionadoDetalle!!,
            onDismiss = { perfilSeleccionadoDetalle = null }
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun AbogadoDirectorioCard(
    abogado: PerfilAbogadoDataModel,
    onVerDetalle: () -> Unit
) {
    val uriHandler = LocalUriHandler.current

    Card(
        modifier = Modifier.fillMaxWidth().clickable { onVerDetalle() },
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            modifier = Modifier.padding(18.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Avatar con iniciales
                Box(
                    modifier = Modifier
                        .size(54.dp)
                        .background(VerdeVerificado.copy(alpha = 0.12f), CircleShape)
                        .border(2.dp, VerdeVerificado, CircleShape),
                    contentAlignment = Alignment.Center
                ) {
                    val iniciales = abogado.nombreCompleto
                        .ifBlank { "Dr" }
                        .split(" ")
                        .take(2)
                        .joinToString("") { it.take(1).uppercase() }
                    Text(
                        text = iniciales,
                        style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                        color = VerdeVerificado
                    )
                }

                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(
                            text = abogado.nombreCompleto.ifBlank { "Dr./Dra. Profesional" },
                            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                            color = MaterialTheme.colorScheme.onSurface,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                        Icon(
                            imageVector = Icons.Default.Verified,
                            contentDescription = "Verificado",
                            tint = VerdeVerificado,
                            modifier = Modifier.size(18.dp)
                        )
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                        if (!abogado.matricula.isNullOrBlank()) {
                            Text(
                                text = "Mat. ${abogado.matricula}",
                                style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold),
                                color = VerdeVerificado
                            )
                        }
                        Text(
                            text = "• ${abogado.jurisdiccion.ifBlank { "Rosario" }}",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            }

            // Especialidades Chips
            if (abogado.especialidades.isNotEmpty()) {
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    abogado.especialidades.take(3).forEach { esp ->
                        Surface(
                            shape = RoundedCornerShape(12.dp),
                            color = VerdeVerificado.copy(alpha = 0.08f)
                        ) {
                            Text(
                                text = esp,
                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp),
                                style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.SemiBold),
                                color = VerdeVerificado
                            )
                        }
                    }
                    if (abogado.especialidades.size > 3) {
                        Surface(shape = RoundedCornerShape(12.dp), color = MaterialTheme.colorScheme.surfaceVariant) {
                            Text(
                                text = "+${abogado.especialidades.size - 3}",
                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp),
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }

            // Biografía / Presentación breve
            if (abogado.biografia.isNotBlank()) {
                Text(
                    text = abogado.biografia,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
            }

            // Acciones de Contacto Directo
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OutlinedButton(
                    onClick = onVerDetalle,
                    modifier = Modifier.weight(1f).height(42.dp),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Text("Ver Perfil", fontSize = 12.sp)
                }

                val telefono = abogado.telefono.orEmpty()
                Button(
                    onClick = {
                        if (telefono.isNotBlank()) {
                            val cleanedPhone = telefono.replace(Regex("[^0-9]"), "")
                            val waUrl = if (cleanedPhone.length >= 10) {
                                if (cleanedPhone.startsWith("54")) "https://wa.me/$cleanedPhone"
                                else "https://wa.me/549$cleanedPhone"
                            } else {
                                "https://wa.me/549$cleanedPhone"
                            }
                            uriHandler.openUri(waUrl)
                        } else {
                            onVerDetalle()
                        }
                    },
                    modifier = Modifier.weight(1.3f).height(42.dp),
                    shape = RoundedCornerShape(12.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = VerdeVerificado)
                ) {
                    Icon(Icons.Default.PhoneInTalk, contentDescription = null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(6.dp))
                    Text("Contactar", fontWeight = FontWeight.Bold, fontSize = 12.sp)
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun FichaPerfilAbogadoDialog(
    abogado: PerfilAbogadoDataModel,
    onDismiss: () -> Unit
) {
    val uriHandler = LocalUriHandler.current

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Icon(Icons.Default.Verified, contentDescription = null, tint = VerdeVerificado, modifier = Modifier.size(24.dp))
                Text(
                    text = abogado.nombreCompleto.ifBlank { "Perfil Profesional" },
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold)
                )
            }
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Surface(
                    shape = RoundedCornerShape(8.dp),
                    color = VerdeVerificado.copy(alpha = 0.1f)
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        Icon(Icons.Default.Badge, contentDescription = null, tint = VerdeVerificado, modifier = Modifier.size(16.dp))
                        Text(
                            text = "Matrícula Verificada: ${abogado.matricula?.ifBlank { "Validadas" } ?: "Validada"}",
                            style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                            color = VerdeVerificado
                        )
                    }
                }

                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    Icon(Icons.Default.LocationOn, contentDescription = null, tint = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.size(16.dp))
                    Text(
                        text = "Jurisdicción: ${abogado.jurisdiccion.ifBlank { "Rosario - Santa Fe" }}",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                if (abogado.especialidades.isNotEmpty()) {
                    Text(
                        text = "Especialidades:",
                        style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold)
                    )
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        verticalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        abogado.especialidades.forEach { esp ->
                            Surface(
                                shape = RoundedCornerShape(12.dp),
                                color = VerdeVerificado.copy(alpha = 0.1f)
                            ) {
                                Text(
                                    text = esp,
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                    style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.SemiBold),
                                    color = VerdeVerificado
                                )
                            }
                        }
                    }
                }

                if (abogado.biografia.isNotBlank()) {
                    Text(
                        text = "Presentación Profesional:",
                        style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold)
                    )
                    Text(
                        text = abogado.biografia,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }
            }
        },
        confirmButton = {
            val telefono = abogado.telefono.orEmpty()
            Button(
                onClick = {
                    if (telefono.isNotBlank()) {
                        val cleanedPhone = telefono.replace(Regex("[^0-9]"), "")
                        val waUrl = if (cleanedPhone.length >= 10) {
                            if (cleanedPhone.startsWith("54")) "https://wa.me/$cleanedPhone"
                            else "https://wa.me/549$cleanedPhone"
                        } else {
                            "https://wa.me/549$cleanedPhone"
                        }
                        uriHandler.openUri(waUrl)
                    }
                },
                colors = ButtonDefaults.buttonColors(containerColor = VerdeVerificado),
                enabled = telefono.isNotBlank()
            ) {
                Icon(Icons.Default.PhoneInTalk, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(6.dp))
                Text("Contactar por WhatsApp")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cerrar")
            }
        }
    )
}
